import {
  format,
  lastDayOfMonth,
  lastDayOfYear,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'
import { t } from 'i18next'

import { FilterFields, FilterQueryProps } from '@model/FilterFields'

import { Operator, SearchQueryKeys } from './ApiClient'

export enum DateFilterPresets {
  Today = 1,
  Yesterday,
  Last7Days,
  Last14Days,
  ThisMonth,
  LastMonth,
  Last3Months,
  Last6Months,
  LastYear,
  Custom,
  ThisWeek,
  ThisYear,
  All,
}

export interface IDateFilter {
  id: number
  value: string | [string, string]
  name: string
  operator?: Operator
  field?: 'DateFrom' | 'DateTo'
}

interface SearchFieldRange {
  from: FilterFields
  to: FilterFields
}

const searchFields: SearchFieldRange[] = [
  { from: FilterFields.DateFrom, to: FilterFields.DateTo },
  { from: FilterFields.ClientDateFrom, to: FilterFields.ClientDateTo },
  { from: FilterFields.AmountFrom, to: FilterFields.AmountTo },
  { from: FilterFields.ClientClientAmountFrom, to: FilterFields.ClientClientAmountTo },
  { from: FilterFields.QueryAmountFrom, to: FilterFields.QueryAmountTo },
  { from: FilterFields.QueryVolumeFrom, to: FilterFields.QueryVolumeTo },
  { from: FilterFields.ClientVolumeFrom, to: FilterFields.ClientVolumeTo },
  { from: FilterFields.ClientVolumeFrom, to: FilterFields.ClientVolumeTo },
  { from: FilterFields.ClientPriceFrom, to: FilterFields.ClientPriceTo },
  { from: FilterFields.ClientDateFrom, to: FilterFields.ClientDateTo },
  { from: FilterFields.ClientRegistrationDateFrom, to: FilterFields.ClientRegistrationDateTo },
]

export function getAppliedFiltersLength<T extends SearchQueryKeys>(search: T): number {
  const searchCopy = Object.assign(search, {}) // do not operate on origin search object
  const searchKeys = Object.keys(searchCopy || {})

  const removeSameFilterType = ({ from, to }: SearchFieldRange) => {
    if (searchKeys.includes(from) && searchKeys.includes(to)) {
      delete searchCopy[to]
    }
  }

  searchFields.forEach(removeSameFilterType)

  if (searchKeys.includes('nested')) {
    delete searchCopy['nested']
  }
  const flatMap = Object.values(searchCopy || {})
    .filter((x) => !!x?.toString())
    .map((a) => {
      if (typeof a === 'number') {
        return a.toString().split(',')
      }
      if (typeof a === 'string' || a instanceof String) {
        return a.split(',')
      }
      // that means it's extended filter { name, operator }
      return [a?.value]
    })
  let filtersLengthApplied = 0
  flatMap.forEach((x) => {
    filtersLengthApplied = filtersLengthApplied + x.length
  })
  return filtersLengthApplied
}

export function getDateTime(): IDateFilter[] {
  const dates: IDateFilter[] = []
  dates.push({
    id: DateFilterPresets.Today,
    name: t('Today'),
    operator: Operator.EQUAL,
    value: format(new Date(), 'MM/dd/yyyy'),
  })
  dates.push({
    id: DateFilterPresets.Yesterday,
    name: t('Yesterday'),
    operator: Operator.EQUAL,
    value: format(subDays(new Date(), 1), 'MM/dd/yyyy'),
  })
  dates.push({
    id: DateFilterPresets.Last7Days,
    name: t('Last 7 days'),
    value: format(subDays(new Date(), 7), 'MM/dd/yyyy'),
  })
  dates.push({
    id: DateFilterPresets.Last14Days,
    name: t('Last 14 days'),
    value: format(subDays(new Date(), 14), 'MM/dd/yyyy'),
  })
  dates.push({
    id: DateFilterPresets.ThisMonth,
    name: t('This month'),
    value: [
      format(startOfMonth(subMonths(new Date(), 0)), 'MM/dd/yyyy'),
      format(new Date(), 'MM/dd/yyyy'),
    ],
  })
  dates.push({
    id: DateFilterPresets.LastMonth,
    name: t('Last month'),
    value: [
      format(startOfMonth(subMonths(new Date(), 1)), 'MM/dd/yyyy'),
      format(lastDayOfMonth(subMonths(new Date(), 1)), 'MM/dd/yyyy'),
    ],
  })
  dates.push({
    id: DateFilterPresets.Last3Months,
    name: t('Last 3 months'),
    value: [
      format(startOfMonth(subMonths(new Date(), 3)), 'MM/dd/yyyy'),
      format(lastDayOfMonth(subMonths(new Date(), 1)), 'MM/dd/yyyy'),
    ],
  })
  dates.push({
    id: DateFilterPresets.Last6Months,
    name: t('Last 6 months'),
    value: [
      format(startOfMonth(subMonths(new Date(), 6)), 'MM/dd/yyyy'),
      format(lastDayOfMonth(subMonths(new Date(), 1)), 'MM/dd/yyyy'),
    ],
  })
  dates.push({
    id: DateFilterPresets.LastYear,
    name: t('Last year'),
    value: [
      format(startOfYear(subYears(new Date(), 1)), 'MM/dd/yyyy'),
      format(lastDayOfYear(subYears(new Date(), 1)), 'MM/dd/yyyy'),
    ],
  })
  dates.push({
    id: DateFilterPresets.Custom,
    name: t('Custom'),
    value: ['', ''],
  })
  return dates
}

export const prefixFilter = ({
  prefix,
  value,
}: {
  prefix?: string
  value?: string | number
}): string | undefined => {
  if (!value) {
    return undefined
  }
  return prefix + '__' + value.toString()
}

export const isAnyFilterActive = (search: FilterQueryProps, searchTerm?: string | null) =>
  Object.values(search || {}).some((value) => !!value) || !!searchTerm
